import { DataType, InverterData, InverterDataComposer, ReadWrite, RegisterTable } from "../lib/inverter.model";
import { getCountryNameFromCodeV3 } from "./country-code";

export const V3WizardVersionInfo: Partial<RegisterTable> = {
  SN: {
    address: 0x8020,
    name: 'Serial_Number0-7',
    dataType: DataType.ASCII,
    description: 'Serial number',
    access: ReadWrite.Read,
    quantity: 10,
  },
  ARM: {
    address: 0x044f,
    name: 'arm',
    dataType: DataType.ASCII,
    description: 'Communication CPU Software Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  DSPM: {
    address: 0x044f + 0x0004,
    name: 'dspm',
    dataType: DataType.ASCII,
    description: 'Master Software Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  DSPS: {
    address: 0x044f + 0x0008,
    name: 'dsps',
    dataType: DataType.ASCII,
    description: 'Vice Software Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  SAFETY_FIRMWARE_VERSION: {
    address: 0x0460,
    name: 'safety_firmware_version',
    dataType: DataType.ASCII,
    description: 'Safety Firmware Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  MODBUS_PROTOCOL_VERSION: {
    address: 0x0044,
    name: 'MODBUS_PROTOCOL_VERSION',
    dataType: DataType.BCD16,
    description: 'Modbus Protocol Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
  COUNTRY: {
    address: 0x2004,
    name: 'COUNTRY',
    dataType: DataType.U16,
    description: 'Country',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  SAFETY_VERSION: {
    address: 0x2005,
    name: 'SAFETY_VERSION',
    dataType: DataType.U16,
    description: 'Safety Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
};

export const V3SafetyConfig: Partial<RegisterTable> = {
  COUNTRY: {
    address: 0x2004,
    name: 'COUNTRY',
    dataType: DataType.U16,
    description: 'Country',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  SAFETY_VERSION: {
    address: 0x2005,
    name: 'SAFETY_VERSION',
    dataType: DataType.U16,
    description: 'Safety Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
};

export const V3BatteryWizardVersionInfo: Partial<RegisterTable> = {
  PCU_Software_Version: {
    address: 0x601B,
    name: 'PCU_Software_Version',
    dataType: DataType.U32,
    description: 'PCU_Software_Version',
    access: ReadWrite.Read,
    quantity: 2,
  },
  Software_Version_Stage_BDU: {
    address: 0x609B,
    name: 'Software_Version_Stage_BDU',
    dataType: DataType.ASCII,
    description: 'Software_Version_Stage_BDU',
    access: ReadWrite.Read,
    quantity: 1,
  },
  Software_Version_Major_BDU: {
    address: 0x609C,
    name: 'Software_Version_Major_BDU',
    dataType: DataType.ASCII,
    description: 'Software_Version_Major_BDU',
    access: ReadWrite.Read,
    quantity: 1,
  },
  Software_Version_Custom_BDU: {
    address: 0x609D,
    name: 'Software_Version_Custom_BDU',
    dataType: DataType.ASCII,
    description: 'Software_Version_Custom_BDU',
    access: ReadWrite.Read,
    quantity: 1,
  },
  Software_Version_Minor_BDU: {
    address: 0x609E,
    name: 'Software_Version_Minor_BDU',
    dataType: DataType.ASCII,
    description: 'Software_Version_Minor_BDU',
    access: ReadWrite.Read,
    quantity: 1,
  },
  BMS_Version: {
    address: 0x900B,
    name: 'BMS_Version',
    dataType: DataType.U16,
    description: 'BMS version number',
    access: ReadWrite.Read,
    quantity: 1,
  },
};

export const V3BatteryProtocolWizardVersionInfo: Partial<RegisterTable> = {
  Battery_Protocol: {
    address: 0x1046,
    name: 'Battery configuration protocol',
    dataType: DataType.U16,
    unit: '',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: 'Battery configuration protocol [0-65535]',
    quantity: 1,
  },
};

export const ComposeV3WizardDeviceCountryCode: Partial<InverterDataComposer> = {
  COUNTRY: (data: Partial<InverterData>) => {
    const value = data.COUNTRY;
    if (value === undefined || typeof value !== 'number') {
      data.COUNTRY = `Not Available`;
      return data;
    }
    data.COUNTRY = getCountryNameFromCodeV3(value);
    return data;
  },
};

export const V3WizardSafetyCountryAndVersion: Partial<RegisterTable> = {
    Safety_Country_Code: {
    address: 0x2004,
    name: 'Safety country',
    dataType: DataType.U16,
    unit: '',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: 'Safety country, used for storage',
    quantity: 1,
  },
  Safety_Version: {
    address: 0x2005,
    name: 'Safety version',
    dataType: DataType.U16,
    unit: '',
    accuracy: 1,
    access: ReadWrite.ReadWrite,
    description: 'The version number of the safety parameters of the communication host (upper computer).',
    quantity: 1,
  },
};

export const ComposeV3BatteryWizardVersion: Partial<InverterDataComposer> = {
  PCU_Software_Version: (data: Partial<InverterData>) => {
    /**
     * 601B and 601C are two registers containing different datatypes,
     * so we treat both as U32 and convert the first to a string and the second to an integer
     */
    const value = data.PCU_Software_Version;
    if (value === undefined || typeof value !== 'number') {
      data.PCU_Software_Version = `Not Available`;
      return data;
    }
    data.PCU_Software_Version = value.toString(16)
      .split(/(?=(?:..)*$)/)
      .map((v, index) => {
        if (index === 0) {
          return String.fromCodePoint(parseInt(v, 16));
        }
        return `${parseInt(v, 16)}`.padStart(2, '0');
      }).join('');
    return data;
  },
  BDU_Software_Version: (data: Partial<InverterData>) => {
    const Software_Version_Stage_BDU = data.Software_Version_Stage_BDU;
    const Software_Version_Major_BDU = data.Software_Version_Major_BDU;
    const Software_Version_Custom_BDU = data.Software_Version_Custom_BDU;
    const Software_Version_Minor_BDU = data.Software_Version_Minor_BDU;

    if (Software_Version_Stage_BDU === undefined || typeof Software_Version_Stage_BDU !== 'string') {
      data.BDU_Software_Version = `Not Available`;
    }
    if (Software_Version_Major_BDU === undefined || typeof Software_Version_Major_BDU !== 'string') {
      data.BDU_Software_Version = `Not Available`;
    }
    if (Software_Version_Custom_BDU === undefined || typeof Software_Version_Custom_BDU !== 'string') {
      data.BDU_Software_Version = `Not Available`;
    }
    if (Software_Version_Minor_BDU === undefined || typeof Software_Version_Minor_BDU !== 'string') {
      data.BDU_Software_Version = `Not Available`;
    }
    data.BDU_Software_Version = `${
      Software_Version_Stage_BDU
    }${Software_Version_Major_BDU}${Software_Version_Custom_BDU}${Software_Version_Minor_BDU}`;
    return data;
  },
  BMS_Version: (data: Partial<InverterData>) => {
    const value = data.BMS_Version;
    if (value === undefined || typeof value !== 'number') {
      data.BMS_Version = `Not Available`;
      return data;
    }
    data.BMS_Version = `V${value.toString().padStart(6, '0')}`;
    return data;
  },
};
